import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Shield, User, Search, Edit, RefreshCw, Building2, Briefcase, HardHat, UserCog, Crown, Eye, EyeOff, Users as UsersIcon, Key } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

// Configuración de roles (4 roles: superadmin, admin, legales, scout)
const ROLE_CONFIG = {
  superadmin: { label: 'SuperAdmin', color: 'bg-red-100 text-red-800', icon: Crown, description: 'Acceso total al sistema + gestión de usuarios' },
  admin: { label: 'Admin', color: 'bg-blue-100 text-blue-800', icon: Shield, description: 'Acceso total excepto gestión de usuarios' },
  legales: { label: 'Legales', color: 'bg-violet-100 text-violet-800', icon: Briefcase, description: 'Contratos y documentos legales' },
  scout: { label: 'Scout', color: 'bg-cyan-100 text-cyan-800', icon: Search, description: 'Seguimiento, jugadores, materiales' },
};

const Users = () => {
  usePageTitle('Usuarios');
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'scout',
    full_name: '',
    assigned_categories: []
  });
  const [editUser, setEditUser] = useState({
    email: '',
    role: '',
    full_name: '',
    assigned_categories: []
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchCategories();
  }, [showInactive]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users?include_inactive=${showInactive}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const data = response.data.data || response.data;
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success('Usuario creado exitosamente');
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      // Solo enviar campos que cambiaron
      const updateData = {};
      if (editUser.email !== selectedUser.email) updateData.email = editUser.email;
      if (editUser.role !== selectedUser.role) updateData.role = editUser.role;
      if (editUser.full_name !== (selectedUser.full_name || '')) updateData.full_name = editUser.full_name;
      if (JSON.stringify(editUser.assigned_categories) !== JSON.stringify(selectedUser.assigned_categories || [])) {
        updateData.assigned_categories = editUser.assigned_categories;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('No hay cambios para guardar');
        setShowEditDialog(false);
        return;
      }

      await axios.put(`${API}/auth/users/${selectedUser.id}`, updateData);
      toast.success('Usuario actualizado exitosamente');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`¿Estás seguro de desactivar al usuario "${username}"?\n\nEl usuario no podrá iniciar sesión pero sus datos asociados se mantendrán.`)) {
      try {
        await axios.delete(`${API}/auth/users/${userId}`);
        toast.success('Usuario desactivado exitosamente');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error(error.response?.data?.detail || 'Error al desactivar usuario');
      }
    }
  };

  const handleReactivateUser = async (userId, username) => {
    if (window.confirm(`¿Reactivar al usuario "${username}"?`)) {
      try {
        await axios.post(`${API}/auth/users/${userId}/reactivate`);
        toast.success('Usuario reactivado exitosamente');
        fetchUsers();
      } catch (error) {
        console.error('Error reactivating user:', error);
        toast.error(error.response?.data?.detail || 'Error al reactivar usuario');
      }
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/users/${selectedUser.id}/change-password`, {
        new_password: newPassword
      });
      toast.success(`Contraseña de ${selectedUser.full_name || selectedUser.username} actualizada`);
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      email: user.email,
      role: user.role,
      full_name: user.full_name || '',
      assigned_categories: user.assigned_categories || []
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setNewUser({
      username: '',
      email: '',
      password: '',
      role: 'scout',
      full_name: '',
      assigned_categories: []
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle both ISO datetime and date-only formats
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getRoleConfig = (role) => {
    // Roles legacy mapeados
    const LEGACY = { gerencia: 'admin', intendente: 'admin', coach: 'scout', administracion: 'admin', user: 'admin' };
    const effective = ROLE_CONFIG[role] ? role : (LEGACY[role] || 'scout');
    return ROLE_CONFIG[effective] || ROLE_CONFIG.scout;
  };

  // Filter and pagination
  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Helper para toggle de categorías asignadas
  const toggleCategory = (categoryId, isEdit = false) => {
    if (isEdit) {
      setEditUser(prev => ({
        ...prev,
        assigned_categories: prev.assigned_categories.includes(categoryId)
          ? prev.assigned_categories.filter(id => id !== categoryId)
          : [...prev.assigned_categories, categoryId]
      }));
    } else {
      setNewUser(prev => ({
        ...prev,
        assigned_categories: prev.assigned_categories.includes(categoryId)
          ? prev.assigned_categories.filter(id => id !== categoryId)
          : [...prev.assigned_categories, categoryId]
      }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="mt-4 text-gray-600">Cargando usuarios...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Usuarios</h1>
            <p className="text-gray-600 mt-1">{filteredUsers.length} usuario(s) encontrado(s)</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-brand-600 hover:bg-brand-700"
            data-testid="create-user-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Buscar usuario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-user-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="showInactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                />
                <Label htmlFor="showInactive" className="text-sm text-gray-600 cursor-pointer">
                  Mostrar inactivos
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {users.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No hay usuarios registrados</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Creado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedUsers.map(user => {
                      const roleConfig = getRoleConfig(user.role);
                      const RoleIcon = roleConfig.icon;
                      const isInactive = user.status === 'inactive';
                      
                      return (
                        <tr 
                          key={user.id} 
                          className={`hover:bg-gray-50 ${isInactive ? 'bg-gray-50 opacity-60' : ''}`} 
                          data-testid={`user-row-${user.id}`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar className={`h-8 w-8 ${isInactive ? 'grayscale' : ''}`}>
                                <AvatarFallback className="bg-brand-100 text-brand-700 text-xs">
                                  {user.username.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">{user.full_name || user.username}</p>
                                {user.full_name && (
                                  <p className="text-xs text-gray-500">{user.username}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge className={roleConfig.color} title={roleConfig.description}>
                              <RoleIcon className="w-3 h-3 mr-1 inline" />
                              {roleConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isInactive ? (
                              <Badge className="bg-red-100 text-red-800">
                                <EyeOff className="w-3 h-3 mr-1 inline" />
                                Inactivo
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800">
                                <Eye className="w-3 h-3 mr-1 inline" />
                                Activo
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created_at)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-brand-600 hover:bg-brand-50"
                                onClick={() => openEditDialog(user)}
                                data-testid={`edit-user-${user.id}`}
                                title="Editar usuario"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:bg-amber-50"
                                onClick={() => openPasswordDialog(user)}
                                data-testid={`password-user-${user.id}`}
                                title="Cambiar contraseña"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              {isInactive ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:bg-green-50"
                                  onClick={() => handleReactivateUser(user.id, user.username)}
                                  data-testid={`reactivate-user-${user.id}`}
                                  title="Reactivar usuario"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              ) : user.role !== 'superadmin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(user.id, user.username)}
                                  data-testid={`delete-user-${user.id}`}
                                  title="Desactivar usuario"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredUsers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              itemName="usuario(s)"
            />
          </Card>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription>
              Crea credenciales de acceso para un nuevo usuario del sistema
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Usuario *</Label>
                  <Input
                    id="username"
                    placeholder="nombre.usuario"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    required
                    data-testid="user-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nombre Completo</Label>
                  <Input
                    id="full_name"
                    placeholder="Juan Pérez"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    data-testid="user-fullname-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Contraseña segura"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rol *</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({...newUser, role: value, assigned_categories: []})}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="superadmin">SuperAdmin - Acceso total</SelectItem>}
                    <SelectItem value="admin">Admin - Todo excepto usuarios</SelectItem>
                    <SelectItem value="legales">Legales - Contratos y documentos</SelectItem>
                    <SelectItem value="scout">Scout - Seguimiento y jugadores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Categorías asignadas - solo para scout */}
              {newUser.role === 'scout' && (
                <div className="space-y-2">
                  <Label>Categorías Asignadas</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    El entrenador solo podrá ver jugadores de estas categorías
                  </p>
                  <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                    {categories.length === 0 ? (
                      <p className="text-sm text-gray-500">No hay categorías disponibles</p>
                    ) : (
                      categories.map(cat => (
                        <div key={cat.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`cat-${cat.id}`}
                            checked={newUser.assigned_categories.includes(cat.id)}
                            onCheckedChange={() => toggleCategory(cat.id)}
                          />
                          <Label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                            {cat.name}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="submit-create-user"
              >
                Crear Usuario
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              Modifica la información de {selectedUser?.full_name || selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-full-name">Nombre Completo</Label>
              <Input
                id="edit-full-name"
                placeholder="Juan Pérez"
                value={editUser.full_name}
                onChange={(e) => setEditUser({...editUser, full_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({...editUser, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rol</Label>
              <Select
                value={editUser.role}
                onValueChange={(value) => setEditUser({...editUser, role: value})}
                disabled={selectedUser?.role === 'superadmin' && !isSuperAdmin}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && <SelectItem value="superadmin">SuperAdmin</SelectItem>}
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="legales">Legales</SelectItem>
                  <SelectItem value="scout">Scout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Categorías asignadas - solo para scout */}
            {editUser.role === 'scout' && (
              <div className="space-y-2">
                <Label>Categorías Asignadas</Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {categories.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay categorías disponibles</p>
                  ) : (
                    categories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`edit-cat-${cat.id}`}
                          checked={editUser.assigned_categories.includes(cat.id)}
                          onCheckedChange={() => toggleCategory(cat.id, true)}
                        />
                        <Label htmlFor={`edit-cat-${cat.id}`} className="text-sm cursor-pointer">
                          {cat.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setSelectedUser(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateUser}
              className="bg-brand-600 hover:bg-brand-700"
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Nueva contraseña para {selectedUser?.full_name || selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <p className="text-xs text-gray-500">
              La nueva contraseña debe tener al menos 6 caracteres.
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowPasswordDialog(false);
                setSelectedUser(null);
                setNewPassword('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              className="bg-brand-600 hover:bg-brand-700"
              disabled={newPassword.length < 6}
              data-testid="submit-password-change"
            >
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Users;
